import moment from "moment";
import { BaseEntityStore } from "./core/base.entity.store";
import { ImportDataType } from "./admin.imports.store";

import { formatDate } from "app/utils";
import { computed } from "mobx";
import { TagColors } from "app/components/common/DottedTag";
import { BaseCachedEntityStore } from "./core/base.cached.entity.store";

enum ImportCycle {
    weekly = 'weekly',
    monthly = 'monthly',
    quarterly = 'quarterly',
    yearly = 'yearly',
    onceinawhile = 'onceinawhile',
    once = 'once',
}

enum IncomingDataTimeScope {
    na = 'na',
    everything = 'everything',
    kw = 'kw',
    month = 'month',
    year = 'year',
}

export enum DvsTimeScope {
    none = 'none',
    kw = 'kw',
    month = 'month',
    year = 'year',
    ytd = 'ytd',
}

enum DbTarget {
    na = 'na',
    aup = 'aup',
    kvagtms = 'kvagtms',
    kvagt = 'kvagtms',
    agentur_akumaaktionen = 'agentur_akumaaktionen',
    agentur_akumaplaner = 'agentur_akumaplaner',
    agentur_adzempfehlungen = 'agentur_adzempfehlungen',
}

export interface Ansprechpartner {
    name: string;
    company: string;
    email: string;
}

function parseAnsprechpartner(input: string): Ansprechpartner {
    // Parse format: "Name (Company) <email>"
    const nameMatch = input.match(/^([^(]+)/);
    const companyMatch = input.match(/\(([^)]+)\)/);
    const emailMatch = input.match(/<([^>]+)>/);

    return {
        name: nameMatch ? nameMatch[1].trim() : '',
        company: companyMatch ? companyMatch[1].trim() : '',
        email: emailMatch ? emailMatch[1].trim() : ''
    };
}

export type DatenStandGroup = 'core' | 'multi' | 'project' | 'adz';

export enum AggStrategy {
    sum = 'SUM',
    avg = 'AVG',
    count = 'COUNT',
    countDistinct = 'COUNT_DISTINCT',
    max = 'MAX',
    latestWins = 'LATEST_WINS',
    mixed = 'MIXED',
    none = 'NONE',
}

export interface DatenStandType {
    group: DatenStandGroup[];
    dataType: string;
    format: (d: Date) => string;
    cycle: ImportCycle;
    incominDataTimeScope: IncomingDataTimeScope;
    requireKwInput?: boolean;
    dvsTimeScope: DvsTimeScope;
    dbTarget: DbTarget;
    timeSelection?: string;
    apFachlich?: Ansprechpartner;
    apTechnisch?: Ansprechpartner;
    schemaHistory?: string;
    /** Name of the AgtAnalyticsStore subclass, if any */
    analyticsStore?: string;
    /** Database table or view name used by the analytics store */
    dbTable?: string;
    /** Whether the store supports time-range queries */
    supportsTimeRange?: boolean;
    /** Primary aggregation strategy */
    aggStrategy?: AggStrategy;
    /** Methods that perform aggregation, e.g. "aggregateByAgt(min,max) → SUM(field1,field2)" */
    aggregateMethods?: string[];
    /** Methods that return raw/non-aggregated data, e.g. "loadByBm(bm) → raw rows" */
    nonAggregateMethods?: string[];
    /** Fields that can be aggregated, e.g. "field1:SUM", "field2:AVG" */
    aggregatableFields?: string[];
    /** Fields that cannot be aggregated, e.g. "vtnr_id:identifier", "status:categorical" */
    nonAggregatableFields?: string[];
    /** Dimensions by which data can be grouped/filtered, e.g. "vtnr_id:entity", "kw:time-kw" */
    dimensions?: string[];
}



const DatenStandTypes: DatenStandType[] = [
    {
        group: ['core'],
        dataType: 'aup_gp',
        format: (d: Date) => formatDate(d),
        cycle: ImportCycle.weekly,
        incominDataTimeScope: IncomingDataTimeScope.everything,
        dvsTimeScope: DvsTimeScope.kw,
        dbTarget: DbTarget.aup,
        apFachlich: parseAnsprechpartner('Wieck, Karsten (Allianz Beratungs- und Vertriebs-AG) <karsten.wieck@allianz.de>'),
    },
    {
        group: ['core'],
        dataType: 'aup_bestand',
        format: (d: Date) => formatDate(moment(d).startOf('month').toDate()),
        cycle: ImportCycle.monthly,
        incominDataTimeScope: IncomingDataTimeScope.everything,
        dvsTimeScope: DvsTimeScope.month,
        dbTarget: DbTarget.aup,
        apFachlich: parseAnsprechpartner('Wieck, Karsten (Allianz Beratungs- und Vertriebs-AG) <karsten.wieck@allianz.de>'),
    },
    {
        group: ['core'],
        dataType: 'aup_vorjahr',
        format: (d: Date) => formatDate(d),
        cycle: ImportCycle.yearly,
        incominDataTimeScope: IncomingDataTimeScope.everything,
        dvsTimeScope: DvsTimeScope.year,
        dbTarget: DbTarget.aup,
        apFachlich: parseAnsprechpartner('Wieck, Karsten (Allianz Beratungs- und Vertriebs-AG) <karsten.wieck@allianz.de>'),
    },
    {
        group: ['core'],
        dataType: ImportDataType.aladin,
        format: (d: Date) => formatDate(d),
        cycle: ImportCycle.onceinawhile,
        incominDataTimeScope: IncomingDataTimeScope.everything,
        dvsTimeScope: DvsTimeScope.none,
        dbTarget: DbTarget.aup,
        timeSelection: 'na',
        apFachlich: parseAnsprechpartner('Wieland, Detlef (Allianz Beratungs- und Vertriebs-AG) <Detlef.Wieland@allianz.de>'),
    },
    {
        group: ['core'],
        dataType: ImportDataType.aladin2,
        format: (d: Date) => formatDate(d),
        cycle: ImportCycle.onceinawhile,
        incominDataTimeScope: IncomingDataTimeScope.everything,
        dvsTimeScope: DvsTimeScope.none,
        dbTarget: DbTarget.aup,
        timeSelection: 'na',
        apFachlich: parseAnsprechpartner('Wieland, Detlef (Allianz Beratungs- und Vertriebs-AG) <Detlef.Wieland@allianz.de>'),
        schemaHistory: '19.2.2026 kompAv und kompAp sind aus dem csv entfalllen. '
    },
    {
        group: ['project', 'adz'],
        dataType: ImportDataType.amisnow,
        format: (d: Date) => formatDate(d),
        cycle: ImportCycle.onceinawhile,
        incominDataTimeScope: IncomingDataTimeScope.month,
        dvsTimeScope: DvsTimeScope.month,
        dbTarget: DbTarget.kvagtms,
        timeSelection: 'none - in future year?',
        apFachlich: parseAnsprechpartner('Kiesspies, Christian (Allianz Kunde und Markt) <christian.kiesspies@allianz.de>'),
        analyticsStore: 'AgenturAmisNowStore',
        dbTable: 'agentur_amisnowaufgaben',
        supportsTimeRange: true,
        aggStrategy: AggStrategy.sum,
        aggregateMethods: [
            'aggregateByBm(min,max) → SUM(abs_tkh,akuma,azamservices,dssk,elpoko,amisnow_postkorb,gomservice,leadmachine,meineallianz,tkh_interessent,manual,meingeld,summe,davon_read)',
            'getDistinctBMs() → DISTINCT(bm)',
        ],
        nonAggregateMethods: [],
        aggregatableFields: [
            'abs_tkh:SUM', 'akuma:SUM', 'azamservices:SUM', 'dssk:SUM', 'elpoko:SUM',
            'amisnow_postkorb:SUM', 'gomservice:SUM', 'leadmachine:SUM', 'meineallianz:SUM',
            'tkh_interessent:SUM', 'manual:SUM', 'meingeld:SUM', 'summe:SUM', 'davon_read:SUM',
        ],
        nonAggregatableFields: [
            'vtnr_id:identifier', 'bm:time-dimension', 'quote_read:derived-ratio',
        ],
        dimensions: ['vtnr_id:entity', 'bm:time-bm'],
    },
    {
        group: ['project', 'adz'],
        dataType: ImportDataType.leadnowconv,
        format: (d: Date) => formatDate(d),
        cycle: ImportCycle.weekly,
        incominDataTimeScope: IncomingDataTimeScope.everything,
        requireKwInput: true,
        dvsTimeScope: DvsTimeScope.kw,
        dbTarget: DbTarget.kvagtms,
        // timeSelection: 'kw - starting 202422',
        // apFachlich: parseAnsprechpartner('Weishaupt, Jessica (Allianz Beratungs- und Vertriebs-AG) <jessica.weishaupt@allianz.de>'),
        // schemaHistory: '2.2.2026: spalte kommentar rückwirkend für 2026 entfernt.',
        dbTable: 'agentur_leadnowconv',
    },
    {
        group: ['project', 'adz'],
        dataType: ImportDataType.leadnow,
        format: (d: Date) => formatDate(d),
        cycle: ImportCycle.weekly,
        incominDataTimeScope: IncomingDataTimeScope.everything,
        dvsTimeScope: DvsTimeScope.kw,
        dbTarget: DbTarget.kvagtms,
        timeSelection: 'kw - starting 202422',
        apFachlich: parseAnsprechpartner('Weishaupt, Jessica (Allianz Beratungs- und Vertriebs-AG) <jessica.weishaupt@allianz.de>'),
        schemaHistory: '2.2.2026: spalte kommentar rückwirkend für 2026 entfernt.',
        analyticsStore: 'AgtLeadNowStore',
        dbTable: 'agentur_leadnow',
        supportsTimeRange: true,
        aggStrategy: AggStrategy.sum,
        aggregateMethods: [
            'aggregateByKwRange(min,max) → SUM(all 45 product brutto/netto/storno fields), MAX(kw)',
            'getDistinctKWs() → DISTINCT(kw)',
        ],
        nonAggregateMethods: [
            'loadWeeklyByAgt(agtId) → raw rows',
            'loadByKw(kw) → raw rows for single KW',
        ],
        aggregatableFields: [
            'av_brutto:SUM', 'av_netto:SUM', 'av_storno:SUM',
            'avverm_brutto:SUM', 'avverm_netto:SUM', 'avverm_storno:SUM',
            'baufi_brutto:SUM', 'baufi_netto:SUM', 'baufi_storno:SUM',
            'bausp_brutto:SUM', 'bausp_netto:SUM', 'bausp_storno:SUM',
            'bu_brutto:SUM', 'bu_netto:SUM', 'bu_storno:SUM',
            'fsach_brutto:SUM', 'fsach_netto:SUM', 'fsach_storno:SUM',
            'kfz_brutto:SUM', 'kfz_netto:SUM', 'kfz_storno:SUM',
            'kv_brutto:SUM', 'kv_netto:SUM', 'kv_storno:SUM',
            'kzus_brutto:SUM', 'kzus_netto:SUM', 'kzus_storno:SUM',
            'psach_brutto:SUM', 'psach_netto:SUM', 'psach_storno:SUM',
            'reise_brutto:SUM', 'reise_netto:SUM', 'reise_storno:SUM',
            'tier_brutto:SUM', 'tier_netto:SUM', 'tier_storno:SUM',
            'berat_brutto:SUM', 'berat_netto:SUM', 'berat_storno:SUM',
            'verm_brutto:SUM', 'verm_netto:SUM', 'verm_storno:SUM',
            'hba_brutto:SUM', 'hba_netto:SUM', 'hba_storno:SUM',
            'kwk_brutto:SUM', 'kwk_netto:SUM', 'kwk_storno:SUM',
            'total_brutto:SUM', 'total_netto:SUM', 'total_storno:SUM',
            'kw:MAX',
        ],
        nonAggregatableFields: [
            'vtnr_id:identifier', 'agenturstatus:categorical',
        ],
        dimensions: ['vtnr_id:entity', 'kw:time-kw', 'agenturstatus:categorical'],
    },
    {
        group: ['project', 'adz'],
        dataType: ImportDataType.leadnowconv,
        format: (d: Date) => formatDate(d),
        cycle: ImportCycle.weekly,
        incominDataTimeScope: IncomingDataTimeScope.everything,
        requireKwInput: true,
        dvsTimeScope: DvsTimeScope.kw,
        dbTarget: DbTarget.kvagtms,
        timeSelection: 'kw - baseline + 10 synthetic delta weeks',
        apFachlich: parseAnsprechpartner('Weishaupt, Jessica (Allianz Beratungs- und Vertriebs-AG) <jessica.weishaupt@allianz.de>'),
        schemaHistory: 'initial - shares 27-col layout with leadnow; SDW-sourced lead conversion',
        dbTable: 'agentur_leadnowconv',
        supportsTimeRange: true,
        aggStrategy: AggStrategy.sum,
        aggregateMethods: [
            'aggregateByKwRange(min,max) → SUM(brutto, netto, converted, neu_stck, neu_bew)',
        ],
        nonAggregateMethods: [
            'loadByKw(kw) → raw rows for single KW',
        ],
        aggregatableFields: [
            'brutto:SUM', 'netto:SUM', 'converted:SUM', 'neu_stck:SUM', 'neu_bew:SUM',
        ],
        nonAggregatableFields: [
            'vtnr_id:identifier', 'kw:time-dimension',
        ],
        dimensions: ['vtnr_id:entity', 'kw:time-kw'],
    },
    {
        group: ['project', 'adz'],
        dataType: ImportDataType.leadnowkwk,
        format: (d: Date) => formatDate(d),
        cycle: ImportCycle.monthly,
        incominDataTimeScope: IncomingDataTimeScope.everything,
        dvsTimeScope: DvsTimeScope.month,
        dbTarget: DbTarget.kvagtms,
        timeSelection: 'monthly - full snapshot per bm, bm from upload filename',
        schemaHistory: 'initial - KwK buchende VT CSV (VTNR;Agentur;Angelegt am;Abo;Agenturstatus)',
        analyticsStore: 'AgtLeadNowKwkStore',
        dbTable: 'agentur_leadnowkwk',
        supportsTimeRange: false,
        aggStrategy: AggStrategy.latestWins,
        aggregateMethods: [
            'getDistinctBMs() → DISTINCT(bm)',
        ],
        nonAggregateMethods: [
            'findByBm(bm) → raw rows for single BM',
        ],
        aggregatableFields: [],
        nonAggregatableFields: [
            'vtnr_id:identifier', 'bm:time-dimension', 'kwk_status:categorical',
        ],
        dimensions: ['vtnr_id:entity', 'bm:time-bm'],
    },
    {
        group: ['project'],
        dataType: ImportDataType.agenturcluster,
        format: (d: Date) => formatDate(d),
        cycle: ImportCycle.yearly,
        incominDataTimeScope: IncomingDataTimeScope.everything,
        dvsTimeScope: DvsTimeScope.year,
        dbTarget: DbTarget.kvagt,
        timeSelection: 'yearly - via berichte navi',
        apFachlich: parseAnsprechpartner('Wieland, Detlef (Allianz Beratungs- und Vertriebs-AG) <Detlef.Wieland@allianz.de>'),
        schemaHistory: '19.2.2026: wie jedes jahr neues schema.. ',
        analyticsStore: 'AgtClusterStore',
        dbTable: 'agentur_cluster',
        supportsTimeRange: false,
        aggStrategy: AggStrategy.latestWins,
        aggregateMethods: [],
        nonAggregateMethods: [
            'loadByBm(bm) → raw rows',
            'loadAgtByBm(agtId,bm) → single agency',
            'findAll() → all records',
        ],
        aggregatableFields: [],
        nonAggregatableFields: [
            'vtnr_id:identifier', 'bm:time-dimension',
            'ub:categorical', 'vbl:categorical', 'bsg:categorical', 'vas:categorical',
            'kvs:categorical', 'kme:categorical', 'bbs:categorical', 'bfe:categorical', 'fb:categorical',
        ],
        dimensions: ['vtnr_id:entity', 'bm:time-bm'],
    },
    {
        group: ['project', 'adz'],
        dataType: ImportDataType.akumaprime,
        format: (d: Date) => formatDate(d),
        cycle: ImportCycle.weekly,
        incominDataTimeScope: IncomingDataTimeScope.year,
        dvsTimeScope: DvsTimeScope.kw,
        dbTarget: DbTarget.kvagtms,
        timeSelection: 'kw - starting 202411',
        apFachlich: parseAnsprechpartner('Firmin, Julie (Allianz Beratungs- und Vertriebs-AG) <julie.firmin@allianz.de>'),
        apTechnisch: parseAnsprechpartner('Roth, Julia (Allianz Kunde und Markt) <julia.roth@allianz.de>'),
        analyticsStore: 'AgtAkumaPrimeStore',
        dbTable: 'agentur_akumaprime',
        supportsTimeRange: true,
        aggStrategy: AggStrategy.mixed,
        aggregateMethods: [
            'aggregateAgtByKw(agtId,min,max) → SUM(18 count fields), AVG(kundenkontaktquote), MAX(kw)',
            'aggregateByAgt(min,max) → SUM(18 count fields), AVG(kundenkontaktquote), MAX(kw)',
            'aggregateByKw(min,max) → SUM(18 count fields), AVG(kundenkontaktquote)',
            'getDistinctKWs() → DISTINCT(kw)',
        ],
        nonAggregateMethods: [],
        aggregatableFields: [
            'einordnung_total:SUM', 'eingeordnet_total:SUM', 'einordnung_sach:SUM',
            'einordnung_leben:SUM', 'einordnung_kranken:SUM', 'einordnung_sonstige:SUM',
            'einordnung_fonds:SUM', 'einordnung_nicht_kontaktieren:SUM',
            'einordnung_bestehende_aktion:SUM', 'einordnung_nicht_eingeordnet:SUM',
            'abwahl:SUM', 'zu_kontaktierende_kunden:SUM',
            'terminierung_agentur:SUM', 'terminierung_extern_total:SUM',
            'beziehung_unbekannt:SUM', 'beziehung_weniger_gut:SUM',
            'beziehung_mittel:SUM', 'beziehung_gut:SUM',
            'kundenkontaktquote:AVG',
        ],
        nonAggregatableFields: [
            'vtnr_id:identifier', 'kw:time-dimension',
            'einordnungsquote:derived-ratio', 'abwahlquote:derived-ratio',
            'terminierung_extern_quote:derived-ratio',
        ],
        dimensions: ['vtnr_id:entity', 'kw:time-kw'],
    },
    {
        group: ['project', 'adz'],
        dataType: ImportDataType.akumaplaner,
        format: (d: Date) => formatDate(d),
        cycle: ImportCycle.weekly,
        incominDataTimeScope: IncomingDataTimeScope.year,
        dvsTimeScope: DvsTimeScope.kw,
        dbTarget: DbTarget.agentur_akumaplaner,
        timeSelection: 'kw - starting 202421',
        apFachlich: parseAnsprechpartner('Firmin, Julie (Allianz Beratungs- und Vertriebs-AG) <julie.firmin@allianz.de>'),
        apTechnisch: parseAnsprechpartner('Roth, Julia (Allianz Kunde und Markt) <julia.roth@allianz.de>'),
        analyticsStore: 'AgtAkumaPlanerStore',
        dbTable: 'agentur_akumaplaner',
        supportsTimeRange: true,
        aggStrategy: AggStrategy.sum,
        aggregateMethods: [
            'aggregateByAgt(min,max) → SUM(zagt,nb,zma,km,nu,zugewiesen,zuweisbar,gpWochen,gpNoWochen), MAX(kw), COUNT_DISTINCT(kw)',
            'aggregateByKw(min,max) → SUM(zagt,nb,zma,km,nu,zugewiesen,zuweisbar)',
            'getDistinctKWs() → DISTINCT(kw)',
        ],
        nonAggregateMethods: [
            'loadWeeklyByAgt(agtId) → raw weekly data for one agency',
        ],
        aggregatableFields: [
            'zagt:SUM', 'nb:SUM', 'zma:SUM', 'km:SUM', 'nu:SUM',
            'zugewiesen:SUM', 'zuweisbar:SUM', 'gpWochen:SUM', 'gpNoWochen:SUM',
            'kw:MAX,COUNT_DISTINCT',
        ],
        nonAggregatableFields: [
            'vtnr_id:identifier', 'quote:derived-ratio',
        ],
        dimensions: ['vtnr_id:entity', 'kw:time-kw'],
    },
    {
        group: ['project', 'adz'],
        dataType: ImportDataType.akumaaktionen,
        format: (d: Date) => formatDate(d),
        cycle: ImportCycle.weekly,
        incominDataTimeScope: IncomingDataTimeScope.year,
        dvsTimeScope: DvsTimeScope.none,
        dbTarget: DbTarget.agentur_akumaaktionen,
        timeSelection: 'none. we only keep the last kw of an aktion',
        apFachlich: parseAnsprechpartner('von Tschammer, Moritz (Allianz Kunde und Markt) <moritz.von-tschammer@allianz.de>'),
        schemaHistory: '20260316: zwei neue spalten: campaign_download and campaign_view ',
        analyticsStore: 'AgtAkumaAktionenStore',
        dbTable: 'agentur_akumaaktionen_calc',
        supportsTimeRange: true,
        aggStrategy: AggStrategy.mixed,
        aggregateMethods: [
            'aggregateByAgt(kw) → SUM(maz,brief,email,inbound,outbound,campaign_count_cont,campaign_count_amistask_agency,campaign_count_amistask_employee), AVG(bearbeitungs_quote), SUM(bearbeitungs_quote_pass), COUNT_DISTINCT(campaign_number)',
            'aggregateByCampaign(kw) → COUNT_DISTINCT(vtnr_id), SUM(campaign_count_cont,campaign_count_amistask_agency,campaign_count_amistask_employee)',
            'aggregateOverviewByAgt(kw) → SUM with CASE on campaign categories',
            'aggregateCampaignCountsByAgt(kw) → COUNT_DISTINCT with CASE',
            'getCampaignDetailsByAgency(nr) → SUM + COUNT_DISTINCT with CASE',
            'getDistinctKWs() → DISTINCT(kw)',
        ],
        nonAggregateMethods: [
            'findByAgenturAndKW(agtId,kw) → raw rows',
            'getActiveAgenturen(kw) → raw filtered by min MAZ',
        ],
        aggregatableFields: [
            'maz:SUM', 'brief:SUM', 'email:SUM', 'inbound:SUM', 'outbound:SUM',
            'campaign_count_cont:SUM', 'campaign_count_amistask_agency:SUM',
            'campaign_count_amistask_employee:SUM', 'bearbeitungs_quote:AVG',
            'bearbeitungs_quote_pass:SUM', 'campaign_number:COUNT_DISTINCT', 'vtnr_id:COUNT_DISTINCT',
        ],
        nonAggregatableFields: [
            'vtnr_id:identifier', 'kw:time-dimension',
            'campaign_number:identifier', 'grenzwert_bearbeitete_aktion:threshold-config',
        ],
        dimensions: ['vtnr_id:entity', 'kw:time-kw', 'campaign_number:campaign'],
    },
    {
        group: ['project', 'adz'],
        dataType: ImportDataType.digitalreport,
        format: (d: Date) => formatDate(d),
        cycle: ImportCycle.monthly,
        incominDataTimeScope: IncomingDataTimeScope.everything,
        requireKwInput: true,
        dvsTimeScope: DvsTimeScope.none,
        dbTarget: DbTarget.kvagt,
        timeSelection: 'monthly - starting 202501. ab 202601 neue spalalte berichtsmonat im csv.',
        apFachlich: parseAnsprechpartner('Husseini, Dr. Ryad (Allianz Kunde und Markt) <RYAD.HUSSEIN@ALLIANZ.DE>'),
        analyticsStore: 'AgtDigitalReportStore',
        dbTable: 'agentur_digitalreport',
        supportsTimeRange: true,
        aggStrategy: AggStrategy.mixed,
        aggregateMethods: [
            'loadMazHistoryByBm() → SUM(anz_maz,anz_maz_18_70,anz_maz_ng)',
            'getDistinctBMs() → DISTINCT(bm)',
        ],
        nonAggregateMethods: [
            'loadByBm(bm) → raw rows',
            'findAll() → all records',
            'findByAgtId(agtId) → raw for agency',
        ],
        aggregatableFields: [
            'anz_maz:SUM', 'anz_maz_18_70:SUM', 'anz_maz_ng:SUM',
        ],
        nonAggregatableFields: [
            'vtnr_id:identifier', 'bm:time-dimension',
            'anz_knd:snapshot', 'anz_digital_id:snapshot', 'ant_digital_id:percentage',
            'ant_maz:percentage', 'anz_wewe:snapshot', 'ant_wewe:percentage',
        ],
        dimensions: ['vtnr_id:entity', 'bm:time-bm'],
    },
    {
        group: ['core'],
        dataType: ImportDataType.multisagentur,
        format: (d: Date) => formatDate(d),
        cycle: ImportCycle.onceinawhile,
        incominDataTimeScope: IncomingDataTimeScope.everything,
        dvsTimeScope: DvsTimeScope.none,
        dbTarget: DbTarget.aup,
        timeSelection: 'na',
        apFachlich: parseAnsprechpartner('Lörch, Matthias (Allianz Beratungs- und Vertriebs-AG) <matthias.loerch@allianz.de>'),
    },
    {
        group: ['core'],
        dataType: ImportDataType.multisgp,
        format: (d: Date) => formatDate(d),
        cycle: ImportCycle.onceinawhile,
        incominDataTimeScope: IncomingDataTimeScope.everything,
        dvsTimeScope: DvsTimeScope.year,
        dbTarget: DbTarget.aup,
        timeSelection: 'na',
        apFachlich: parseAnsprechpartner('Schaal, Cornelia (Allianz Beratungs- und Vertriebs-AG) <cornelia.schaal@allianz.de>'),
    },
    {
        group: ['project', 'adz'],
        dataType: ImportDataType.googleprofile,
        format: (d: Date) => formatDate(d),
        cycle: ImportCycle.monthly,
        incominDataTimeScope: IncomingDataTimeScope.month,
        requireKwInput: true,
        dvsTimeScope: DvsTimeScope.month,
        dbTarget: DbTarget.kvagtms,
        timeSelection: 'monthly - sauber ab 2025. Ist daten',
        apFachlich: parseAnsprechpartner('Hoehne, Jan (Allianz Beratungs- und Vertriebs-AG) <jan.hoehne@allianz.de>'),
        apTechnisch: parseAnsprechpartner('Hecht-Zirpel, Mathis (Allianz Beratungs- und Vertriebs-AG) <mathis.hecht-zirpel@allianz.de>'),
        schemaHistory: '20.11.2025: removed anzahl_fragen. added neue_bewertungen, selbstbau (- 202509: letzte lieferung mit anzahl fragen - 202511: erste lieferung mit neuebewertung & selbstbau )',
        analyticsStore: 'AgtGoogleProfileStore',
        dbTable: 'agentur_googleprofile',
        supportsTimeRange: true,
        aggStrategy: AggStrategy.latestWins,
        aggregateMethods: [
            'getDistinctBMs() → DISTINCT(bm)',
        ],
        nonAggregateMethods: [
            'loadByBm(bm) → raw rows',
            'loadTimeSeriesByAgt(agtId) → raw time series',
            'loadTimeSeriesByBm(min,max) → raw BM range',
        ],
        aggregatableFields: [],
        nonAggregatableFields: [
            'vtnr_id:identifier', 'bm:time-dimension',
            'bewertungen:snapshot-cumulative', 'bewertungsdurchschnitt:snapshot-avg',
            'neue_bewertungen:snapshot', 'anzahl_bilder:snapshot',
            'ranking:ordinal', 'bewertungschange:snapshot-delta',
        ],
        dimensions: ['vtnr_id:entity', 'bm:time-bm'],
    },
    {
        group: ['project', 'adz'],
        dataType: ImportDataType.facebook,
        format: (d: Date) => formatDate(d),
        cycle: ImportCycle.monthly,
        incominDataTimeScope: IncomingDataTimeScope.month,
        dvsTimeScope: DvsTimeScope.month,
        dbTarget: DbTarget.kvagtms,
        timeSelection: 'nur ist - saubere ab 2025. monatlicher selector wäre möglich',
        apFachlich: parseAnsprechpartner('Taupert, Alicia (Allianz Beratungs- und Vertriebs-AG) <alicia.taupert@allianz.de>'),
        apTechnisch: parseAnsprechpartner('Walz, Raphael (Allianz Kunde und Markt, externer Mitarbeiter) <extern.walz.raphael@allianz.de>'),
        analyticsStore: 'AgtFacebookStore',
        dbTable: 'agentur_facebook',
        supportsTimeRange: true,
        aggStrategy: AggStrategy.mixed,
        aggregateMethods: [
            'aggreagteYtd(bm) → SUM(postinganzahl,vt_eigene_posts)',
            'getDistinctBMs() → DISTINCT(bm)',
        ],
        nonAggregateMethods: [
            'loadAgtByBm(agtId,bm) → raw rows',
            'loadByBm(bm) → raw + YTD merged',
        ],
        aggregatableFields: [
            'postinganzahl:SUM', 'vt_eigene_posts:SUM',
        ],
        nonAggregatableFields: [
            'vtnr_id:identifier', 'bm:time-dimension', 'facebook_url:URL',
            'seiten_status:status', 'fananzahl:snapshot', 'abonnenten:snapshot',
            'erreichte_personen:snapshot', 'sichtkontakte:snapshot',
            'abo_pr:percentage', 'abo_1890:categorical',
        ],
        dimensions: ['vtnr_id:entity', 'bm:time-bm'],
    },
    {
        group: ['project', 'adz'],
        dataType: ImportDataType.instagram,
        format: (d: Date) => formatDate(d),
        cycle: ImportCycle.monthly,
        incominDataTimeScope: IncomingDataTimeScope.month,
        dvsTimeScope: DvsTimeScope.month,
        dbTarget: DbTarget.kvagtms,
        timeSelection: 'nur ist - saubere ab 2025. monatlicher selector wäre möglich',
        apFachlich: parseAnsprechpartner('Taupert, Alicia (Allianz Beratungs- und Vertriebs-AG) <alicia.taupert@allianz.de>'),
        apTechnisch: parseAnsprechpartner('Walz, Raphael (Allianz Kunde und Markt, externer Mitarbeiter) <extern.walz.raphael@allianz.de>'),
        analyticsStore: 'AgtInstagramStore',
        dbTable: 'agentur_instagram',
        supportsTimeRange: true,
        aggStrategy: AggStrategy.mixed,
        aggregateMethods: [
            'aggreagteYtd(bm) → SUM(postinganzahl,vt_eigene_posts,views)',
            'getDistinctBMs() → DISTINCT(bm)',
        ],
        nonAggregateMethods: [
            'loadAgtByBm(agtId,bm) → raw rows',
            'loadByBm(bm) → raw + YTD merged',
        ],
        aggregatableFields: [
            'postinganzahl:SUM', 'vt_eigene_posts:SUM', 'views:SUM',
        ],
        nonAggregatableFields: [
            'vtnr_id:identifier', 'bm:time-dimension', 'instagram_url:URL',
            'seiten_status:status', 'follower:snapshot', 'reichweite:snapshot', 'abo_pr:percentage',
        ],
        dimensions: ['vtnr_id:entity', 'bm:time-bm'],
    },
    {
        group: ['project', 'adz'],
        dataType: ImportDataType.tiktok,
        format: (d: Date) => formatDate(d),
        cycle: ImportCycle.monthly,
        incominDataTimeScope: IncomingDataTimeScope.month,
        dvsTimeScope: DvsTimeScope.month,
        dbTarget: DbTarget.kvagtms,
        timeSelection: '12025. monatlicher selector wäre möglich',
        // apFachlich: parseAnsprechpartner('Taupert, Alicia (Allianz Beratungs- und Vertriebs-AG) <alicia.taupert@allianz.de>'),
        // apTechnisch: parseAnsprechpartner('Walz, Raphael (Allianz Kunde und Markt, externer Mitarbeiter) <extern.walz.raphael@allianz.de>'),
        analyticsStore: 'AgtTiktokStore',
        dbTable: 'agentur_tiktok',
        supportsTimeRange: true,
        aggStrategy: AggStrategy.mixed,
        aggregateMethods: [
            'aggregateYtd(bm) → SUM(postinganzahl,sichtkontakte,erreichte_personen)',
            'getDistinctBMs() → DISTINCT(bm)',
        ],
        nonAggregateMethods: [
            'loadAgtByBm(agtId,bm) → raw rows',
            'loadByBm(bm) → raw + YTD merged',
        ],
        aggregatableFields: [
            'postinganzahl:SUM', 'sichtkontakte:SUM', 'erreichte_personen:SUM',
        ],
        nonAggregatableFields: [
            'vtnr_id:identifier', 'bm:time-dimension', 'screen_name:text',
            'account_status:status', 'follower:snapshot',
        ],
        dimensions: ['vtnr_id:entity', 'bm:time-bm'],
    },
    {
        group: ['project', 'adz'],
        dataType: ImportDataType.linkedin,
        format: (d: Date) => formatDate(d),
        cycle: ImportCycle.monthly,
        incominDataTimeScope: IncomingDataTimeScope.month,
        dvsTimeScope: DvsTimeScope.month,
        dbTarget: DbTarget.kvagtms,
        timeSelection: 'nur ist - saubere ab 2025. monatlicher selector wäre möglich',
        apFachlich: parseAnsprechpartner('Taupert, Alicia (Allianz Beratungs- und Vertriebs-AG) <alicia.taupert@allianz.de>'),
        apTechnisch: parseAnsprechpartner('Walz, Raphael (Allianz Kunde und Markt, externer Mitarbeiter) <extern.walz.raphael@allianz.de>'),
        analyticsStore: 'AgtLinkedinStore',
        dbTable: 'agentur_linkedin',
        supportsTimeRange: true,
        aggStrategy: AggStrategy.mixed,
        aggregateMethods: [
            'aggregateYtd(bm) → SUM(postinganzahl,sichtkontakte,erreichte_personen)',
            'getDistinctBMs() → DISTINCT(bm)',
        ],
        nonAggregateMethods: [
            'loadAgtByBm(agtId,bm) → raw rows',
            'loadByBm(bm) → raw + YTD merged',
        ],
        aggregatableFields: [
            'postinganzahl:SUM', 'sichtkontakte:SUM', 'erreichte_personen:SUM',
        ],
        nonAggregatableFields: [
            'vtnr_id:identifier', 'bm:time-dimension', 'account_url:URL',
            'account_status:status', 'follower:snapshot',
        ],
        dimensions: ['vtnr_id:entity', 'bm:time-bm'],
    },
    {
        group: ['project', 'adz'],
        dataType: ImportDataType.kvm,
        format: (d: Date) => formatDate(d),
        cycle: ImportCycle.monthly,
        incominDataTimeScope: IncomingDataTimeScope.everything,
        dvsTimeScope: DvsTimeScope.month,
        dbTarget: DbTarget.kvagtms,
        timeSelection: 'none - data in db and ui store is monthly',
        apFachlich: parseAnsprechpartner('Röder, Yannick (Allianz Beratungs- und Vertriebs-AG) <yannick.roeder@allianz.de>'),
        analyticsStore: 'AgtKvmStore',
        dbTable: 'agentur_kvm',
        supportsTimeRange: true,
        aggStrategy: AggStrategy.sum,
        aggregateMethods: [
            'aggregateByAgt(min,max) → SUM(anzahl_anrufe_gesamt,anzahl_anrufe_waehrend_servicezeiten,ziel1_schaden,ziel2_vertragsanliegen,ziel3_sonstige_anliegen,ziel3_dann_retoure,retoure,keine_taste,schnellaufleger), COUNT_DISTINCT(bm)',
            'getDistinctBMs() → DISTINCT(bm)',
        ],
        nonAggregateMethods: [
            'loadAgtByBm(agtId,bm) → raw rows',
            'loadByBm(bm) → raw rows',
            'loadMonthlyByBmRange(min,max) → raw range',
        ],
        aggregatableFields: [
            'anzahl_anrufe_gesamt:SUM', 'anzahl_anrufe_waehrend_servicezeiten:SUM',
            'ziel1_schaden:SUM', 'ziel2_vertragsanliegen:SUM', 'ziel3_sonstige_anliegen:SUM',
            'ziel3_dann_retoure:SUM', 'retoure:SUM', 'keine_taste:SUM', 'schnellaufleger:SUM',
            'bm:COUNT_DISTINCT',
        ],
        nonAggregatableFields: [
            'vtnr_id:identifier', 'bm:time-dimension',
        ],
        dimensions: ['vtnr_id:entity', 'bm:time-bm'],
    },
    {
        group: ['project', 'adz'],
        dataType: ImportDataType.sts2024,
        format: (d: Date) => formatDate(d),
        cycle: ImportCycle.once,
        incominDataTimeScope: IncomingDataTimeScope.everything,
        dvsTimeScope: DvsTimeScope.year,
        dbTarget: DbTarget.kvagt,
        timeSelection: 'na',
        apFachlich: parseAnsprechpartner('Perisic, Jelena (Allianz Kunde und Markt) <jelena.perisic@allianz.de>'),
        apTechnisch: parseAnsprechpartner('Balac, Ratko (Allianz Kunde und Markt, externer Mitarbeiter) <extern.balac_ratko@allianz.de>'),
        analyticsStore: 'AgtStsStore',
        dbTable: 'agentur_sts',
        supportsTimeRange: false,
        aggStrategy: AggStrategy.latestWins,
        aggregateMethods: [],
        nonAggregateMethods: [
            'loadAll() → raw rows',
            'loadByAgtId(agtId) → raw rows',
            'loadByMaxBm() → latest BM per agency',
            'loadByBm(bm) → raw rows',
            'findByAgtId(agtId) → single record',
        ],
        aggregatableFields: [],
        nonAggregatableFields: [
            'vtnr_id:identifier', 'bm:time-dimension', 'year:time-dimension',
            'bid:identifier', 'vorname:text', 'nachname:text',
            'basic:point-in-time', 'mc:point-in-time',
        ],
        dimensions: ['vtnr_id:entity', 'bm:time-bm', 'bid:person'],
    },
    {
        group: ['project', 'adz'],
        dataType: ImportDataType.sts,
        format: (d: Date) => formatDate(d),
        cycle: ImportCycle.onceinawhile,
        incominDataTimeScope: IncomingDataTimeScope.everything,
        dvsTimeScope: DvsTimeScope.year,
        dbTarget: DbTarget.kvagt,
        timeSelection: 'none - point in time',
        apFachlich: parseAnsprechpartner('Perisic, Jelena (Allianz Kunde und Markt) <jelena.perisic@allianz.de>'),
        apTechnisch: parseAnsprechpartner('Balac, Ratko (Allianz Kunde und Markt, externer Mitarbeiter) <extern.balac_ratko@allianz.de>'),
        schemaHistory: '02.2026: column bm was added. bm data is now is now consitent from 202512 onwards.',
        analyticsStore: 'AgtStsStore',
        dbTable: 'agentur_sts',
        supportsTimeRange: false,
        aggStrategy: AggStrategy.latestWins,
        aggregateMethods: [],
        nonAggregateMethods: [
            'loadAll() → raw rows',
            'loadByAgtId(agtId) → raw rows',
            'loadByMaxBm() → latest BM per agency',
            'loadByBm(bm) → raw rows',
            'findByAgtId(agtId) → single record',
        ],
        aggregatableFields: [],
        nonAggregatableFields: [
            'vtnr_id:identifier', 'bm:time-dimension', 'year:time-dimension',
            'bid:identifier', 'vorname:text', 'nachname:text',
            'basic:point-in-time', 'mc:point-in-time',
        ],
        dimensions: ['vtnr_id:entity', 'bm:time-bm', 'bid:person'],
    },
    {
        group: ['project', 'adz'],
        dataType: ImportDataType.adzempfehlung,
        format: (d: Date) => formatDate(d),
        cycle: ImportCycle.yearly,
        incominDataTimeScope: IncomingDataTimeScope.everything,
        dvsTimeScope: DvsTimeScope.year,
        dbTarget: DbTarget.agentur_adzempfehlungen,
        apFachlich: parseAnsprechpartner('Mueller, Steffen (Allianz Beratungs- und Vertriebs-AG) <steffen.mueller2@allianz.de>'),
        apTechnisch: parseAnsprechpartner('Ripke, Tim <tim.ripke@smp-strategy.de>'),
        analyticsStore: 'AgtAdzEmpfehlungStore',
        dbTable: 'agentur_adzempfehlungen',
        supportsTimeRange: false,
        aggStrategy: AggStrategy.latestWins,
        aggregateMethods: [],
        nonAggregateMethods: [
            'findAll() → all rows',
        ],
        aggregatableFields: [
            'google_profile:SUM', 'social_media_toolbox:SUM', 'amis_now:SUM',
            'service_to_sale:SUM', 'akuma_planer:SUM', 'akuma_prime:SUM',
            'meineallianz:SUM', 'kvm_support:SUM', 'lead_now:SUM',
        ],
        nonAggregatableFields: [
            'vtnr_id:identifier', 'bm:time-dimension',
        ],
        dimensions: ['vtnr_id:entity', 'bm:time-year'],
    },
    {
        group: ['core'],
        dataType: ImportDataType.crawler,
        format: (d: Date) => formatDate(d),
        cycle: ImportCycle.weekly,
        incominDataTimeScope: IncomingDataTimeScope.na,
        dvsTimeScope: DvsTimeScope.none,
        dbTarget: DbTarget.na
    },
    {
        group: ['core'],
        dataType: ImportDataType.crawlerMultigp25,
        format: (d: Date) => formatDate(d),
        cycle: ImportCycle.onceinawhile,
        incominDataTimeScope: IncomingDataTimeScope.na,
        dvsTimeScope: DvsTimeScope.none,
        dbTarget: DbTarget.na
    },
    {
        group: ['project'],
        dataType: ImportDataType.fitnesspdf,
        format: (d: Date) => formatDate(d),
        cycle: ImportCycle.quarterly,
        incominDataTimeScope: IncomingDataTimeScope.na,
        dvsTimeScope: DvsTimeScope.none,
        dbTarget: DbTarget.na,
        apFachlich: parseAnsprechpartner('Mueller, Steffen (Allianz Beratungs- und Vertriebs-AG) <steffen.mueller2@allianz.de>'),
        apTechnisch: parseAnsprechpartner('Ripke, Tim <tim.ripke@smp-strategy.de>'),
    },
    {
        group: ['project'],
        dataType: ImportDataType.wkzgkofferbak,
        format: (d: Date) => formatDate(d),
        cycle: ImportCycle.onceinawhile,
        incominDataTimeScope: IncomingDataTimeScope.na,
        dvsTimeScope: DvsTimeScope.year,
        dbTarget: DbTarget.na
    },
    {
        group: ['project'],
        dataType: ImportDataType.wkzgkofferbak26,
        format: (d: Date) => formatDate(d),
        cycle: ImportCycle.onceinawhile,
        incominDataTimeScope: IncomingDataTimeScope.na,
        dvsTimeScope: DvsTimeScope.year,
        dbTarget: DbTarget.na
    },
    {
        group: ['project'],
        dataType: ImportDataType.wkzgkofferfzw,
        format: (d: Date) => formatDate(d),
        cycle: ImportCycle.onceinawhile,
        incominDataTimeScope: IncomingDataTimeScope.na,
        dvsTimeScope: DvsTimeScope.year,
        dbTarget: DbTarget.na
    },

]


export const FindDatenStandType = (dataType: string): DatenStandType | undefined => {
    return DatenStandTypes.find(ds => ds.dataType === dataType);
}

export class DatenStandModel {
    constructor(opts: any) {
        this.dataType = opts.key;
        if (opts.valueString) {
            const j = JSON.parse(opts.valueString);
            this.file = j.dataPath;
            this.stand = moment(j.datenStand).toDate();
            this.agtCount = j.agtCount;
            this.rowCount = j.rowCount;
        }
        this.modified = opts.modified;
        const thing = DatenStandTypes.find(x => x.dataType === this.dataType);
        if (thing) {
            this.cycle = thing.cycle;
            this.standFormated = thing.format(this.stand!);
            this.incominDataTimeScope = thing.incominDataTimeScope;
            this.dvsTimeScope = thing.dvsTimeScope;
            this.dbTarget = thing.dbTarget;
            this.timeSelection = thing.timeSelection;
            this.apFachlich = thing.apFachlich;
            this.apTechnisch = thing.apTechnisch;
            this.requireKwInput = !!thing.requireKwInput;
            this.group = thing.group;
            this.analyticsStore = thing.analyticsStore;
            this.dbTable = thing.dbTable;
            this.supportsTimeRange = thing.supportsTimeRange;
            this.aggStrategy = thing.aggStrategy;
            this.aggregateMethods = thing.aggregateMethods;
            this.nonAggregateMethods = thing.nonAggregateMethods;
            this.aggregatableFields = thing.aggregatableFields;
            this.nonAggregatableFields = thing.nonAggregatableFields;
            this.dimensions = thing.dimensions;
        }
    }

    dataType: string;
    file?: string;
    stand?: Date;
    standFormated?: string;
    modified?: Date;
    cycle?: ImportCycle;
    incominDataTimeScope?: IncomingDataTimeScope;
    requireKwInput?: boolean;
    dvsTimeScope?: DvsTimeScope;
    dbTarget?: DbTarget;
    timeSelection?: string;
    apFachlich?: Ansprechpartner;
    apTechnisch?: Ansprechpartner;
    group?: DatenStandGroup[];
    analyticsStore?: string;
    dbTable?: string;
    supportsTimeRange?: boolean;
    aggStrategy?: AggStrategy;
    aggregateMethods?: string[];
    nonAggregateMethods?: string[];
    aggregatableFields?: string[];
    nonAggregatableFields?: string[];
    dimensions?: string[];

    agtCount?: number;
    rowCount?: number;

    @computed
    get ageInDays() {
        if (!this.cycle) {
            return -500;
        }
        return moment().diff(moment(this.stand!), 'days');
    }

    @computed
    get importDataType(): ImportDataType {
        if (["aup_gp", "aup_bestand"].includes(this.dataType)) {
            return ImportDataType.aup;
        }
        return this.dataType as ImportDataType;
    }

    @computed
    get ageForSorting() {
        const age = this.ageInDays;
        let goodAge = 7;
        let badAge = 14;
        switch (this.cycle!) {
            case ImportCycle.weekly:
                break;
            case ImportCycle.monthly:
                goodAge = 30;
                badAge = 45;
                break;
            case ImportCycle.yearly:
                goodAge = 365;
                badAge = 400;
                break;
            case ImportCycle.onceinawhile:
                goodAge = 120;
                badAge = 240;
                break;
            default:
                return -500;

        }
        if (age <= goodAge) {
            return age / 10;
            //return TagColors.green;
        }
        if (age < badAge) {
            return age + badAge
            //return TagColors.yellow;
        }
        return age + badAge * 10;
        return TagColors.red;;
    }


    @computed
    get color() {
        const age = this.ageInDays;
        let goodAge = 7;
        let badAge = 14;
        switch (this.cycle!) {
            case ImportCycle.weekly:
                break;
            case ImportCycle.monthly:
                goodAge = 30;
                badAge = 45;
                break;
            case ImportCycle.yearly:
                goodAge = 365;
                badAge = 400;
                break;
            case ImportCycle.onceinawhile:
                goodAge = 120;
                badAge = 240;
                break;

        }
        if (age <= goodAge) {
            return TagColors.green;
        }
        if (age < badAge) {
            return TagColors.yellow;
        }
        return TagColors.red;;
    }

    @computed
    get toSearchString(): string {
        const parts = [
            this.dataType,
            this.file,
            this.standFormated,
            this.cycle,
            this.incominDataTimeScope,
            this.dvsTimeScope,
            this.dbTarget,
            this.timeSelection,
            this.apFachlich?.name,
            this.apFachlich?.company,
            this.apFachlich?.email,
            this.apTechnisch?.name,
            this.apTechnisch?.company,
            this.apTechnisch?.email,
            this.agtCount?.toString(),
            this.rowCount?.toString(),
        ];
        return parts.filter(p => p).join(' ').toLowerCase();
    }
}

export class AdminDatenStandStore extends BaseEntityStore<DatenStandModel> {
    apiPath = 'api/kv/datenstand';
    idProp: string = 'id';
    createInstances(opts: any): DatenStandModel {
        return new DatenStandModel(opts);
    }
}

export class DatenStandStore extends BaseCachedEntityStore<DatenStandModel> {
    apiPath = 'api/kv/datenstand';
    idProp: string = 'id';
    createInstances(opts: any): DatenStandModel {
        return new DatenStandModel(opts);
    }
}
